import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBookings } from '../hooks/useBookings';
import { useServices } from '../hooks/useServices';

function Booking() {
  const navigate = useNavigate();
  const { user, signUp, signIn, isAuthenticated, isEmailVerified } = useAuth();
  const { services } = useServices();
  const { createBooking, isCreating } = useBookings();
  
  const [step, setStep] = useState('login');
  
  // Update step when authentication changes
  React.useEffect(() => {
    console.log('Auth state changed:', { isAuthenticated, user, isEmailVerified })
    if (isAuthenticated && user) {
      if (isEmailVerified) {
        setStep('booking');
      } else {
        setStep('login');
        if (window.shouldShowVerificationMessage) {
          window.shouldShowVerificationMessage = false;
        }
      }
    } else {
      setStep('login');
    }
  }, [isAuthenticated, user, isEmailVerified]);

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    contact: '',
    location: '',
    name: ''
  });

  const [bookingData, setBookingData] = useState({
    service_id: '',
    startDate: '',
    endDate: '',
    guests: 1,
    specialRequests: ''
  });

  const [error, setError] = useState('');
  const [isLogin, setIsLogin] = useState(true);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = loginData.email.toLowerCase().trim(); // Force lowercase + trim

    if (!email || !loginData.name.trim()) {
      setError('Please fill in required fields');
      return;
    }

    if (!isLogin && loginData.password !== loginData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      let result;
      console.log('Attempting authentication:', { isLogin, email });
      
      if (isLogin) {
        result = await signIn(email, loginData.password);
      } else {
        result = await signUp(email, loginData.password, {
          full_name: loginData.name.trim(),
          phone: loginData.contact,
          location: loginData.location
        });
      }

      console.log('Auth result:', result);

      if (result.success) {
        if (isLogin) {
          const userConfirmed = result.data.user && 
            (result.data.user.email_confirmed_at || result.data.user.confirmed_at);
          
          if (userConfirmed) {
            console.log('Email verified, moving to booking step');
            setStep('booking');
          } else {
            setError('Please verify your email address. Check your inbox for the verification email.');
          }
        } else {
          setError('Please check your email to verify your account before signing in.');
        }
      } else {
        setError(result.error?.message || 'Authentication failed. Please try again.');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      setError('Authentication failed. Please try again.');
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!bookingData.service_id || !bookingData.startDate || !bookingData.endDate) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(bookingData.startDate) >= new Date(bookingData.endDate)) {
      setError('End date must be after start date');
      return;
    }

    try {
      const booking = {
        user_id: user.id,
        service_id: bookingData.service_id,
        start_date: bookingData.startDate,
        end_date: bookingData.endDate,
        guests: parseInt(bookingData.guests),
        special_requests: bookingData.specialRequests,
        status: 'pending'
      };

      await createBooking(booking);
      navigate('/'); // Redirect after successful booking
    } catch (error) {
      setError('Failed to submit booking. Please try again.');
    }
  };

  return (
    <div
      className="booking-container"
      style={{
        background: 'url("/4.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        fontFamily: 'system-ui, sans-serif',
        color: 'white',
        padding: '40px 20px',
        paddingRight: '40px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center'
      }}
    >
      <div style={{
        maxWidth: '450px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.3)',
        backdropFilter: 'blur(10px)',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(154, 205, 50, 0.3)'
      }}>
        {step === 'login' && (
          <>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '30px',
              color: '#9acd32'
            }}>
              {isLogin ? 'Login to Book Your Adventure' : 'Create Account to Book'}
            </h2>
            
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9acd32',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '1rem'
                }}
              >
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Login"}
              </button>
            </div>

            {error && (
              <p style={{ color: '#ff6b6b', textAlign: 'center', marginBottom: '20px' }}>
                {error}
              </p>
            )}

            <form onSubmit={handleAuthSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Name
                </label>
                <input
                  type="text"
                  value={loginData.name}
                  onChange={(e) => setLoginData({ ...loginData, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Email
                </label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ 
                    ...loginData, 
                    email: e.target.value.toLowerCase() // Force lowercase on input
                  })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem',
                    textTransform: 'lowercase' // Visual feedback
                  }}
                  placeholder="enter your email"
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Password
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                  placeholder="Enter your password"
                />
                {!isLogin && (
                  <p style={{ marginTop: '5px', fontSize: '0.9rem', opacity: 0.8 }}>
                    After creating your account, you'll receive a verification email. Please verify your email before signing in.
                  </p>
                )}
              </div>
              
              {!isLogin && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={loginData.confirmPassword}
                      onChange={(e) => setLoginData({ ...loginData, confirmPassword: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '10px',
                        border: '1px solid #9acd32',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                      placeholder="Confirm your password"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Contact Number
                    </label>
                    <input
                      type="tel"
                      value={loginData.contact}
                      onChange={(e) => setLoginData({ ...loginData, contact: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '10px',
                        border: '1px solid #9acd32',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                      placeholder="e.g. +255123456789"
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                      Location
                    </label>
                    <input
                      type="text"
                      value={loginData.location}
                      onChange={(e) => setLoginData({ ...loginData, location: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '10px',
                        border: '1px solid #9acd32',
                        background: 'rgba(255, 255, 255, 0.2)',
                        color: 'white',
                        fontSize: '1rem'
                      }}
                      placeholder="City / Country"
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                disabled={isCreating}
                style={{
                  background: '#9acd32',
                  color: '#2f4f2f',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '30px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 5px 15px rgba(154, 205, 50, 0.3)',
                  opacity: isCreating ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isCreating) {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(154, 205, 50, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 5px 15px rgba(154, 205, 50, 0.3)';
                }}
              >
                {isLogin ? 'Login' : 'Create Account'}
              </button>
            </form>
          </>
        )}

        {step === 'booking' && (
          <>
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              textAlign: 'center',
              marginBottom: '30px',
              color: '#9acd32'
            }}>
              Book Your Tanzanian Adventure
            </h2>
            {error && (
              <p style={{ color: '#ff6b6b', textAlign: 'center', marginBottom: '20px' }}>
                {error}
              </p>
            )}
            <form onSubmit={handleBookingSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Select Service
                </label>
                <select
                  value={bookingData.service_id}
                  onChange={(e) => setBookingData({ ...bookingData, service_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                >
                  <option value="">Choose a service</option>
                  {services?.map((service) => (
                    <option key={service.id} value={service.id}>
                      {service.title} - From ${service.price_from}/day
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={bookingData.startDate}
                  onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  End Date
                </label>
                <input
                  type="date"
                  value={bookingData.endDate}
                  onChange={(e) => setBookingData({ ...bookingData, endDate: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Number of Guests
                </label>
                <input
                  type="number"
                  value={bookingData.guests}
                  onChange={(e) => setBookingData({ ...bookingData, guests: Math.max(1, parseInt(e.target.value) || 1) })}
                  min="1"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                  placeholder="1"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Special Requests
                </label>
                <textarea
                  value={bookingData.specialRequests}
                  onChange={(e) => setBookingData({ ...bookingData, specialRequests: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid #9acd32',
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '1rem',
                    minHeight: '100px',
                    resize: 'vertical'
                  }}
                  placeholder="Any dietary needs, preferences, or special requests?"
                />
              </div>

              <button
                type="submit"
                disabled={isCreating}
                style={{
                  background: '#9acd32',
                  color: '#2f4f2f',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '30px',
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 5px 15px rgba(154, 205, 50, 0.3)',
                  opacity: isCreating ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isCreating) {
                    e.target.style.transform = 'translateY(-3px)';
                    e.target.style.boxShadow = '0 8px 25px rgba(154, 205, 50, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 5px 15px rgba(154, 205, 50, 0.3)';
                }}
              >
                {isCreating ? 'Submitting...' : 'Submit Booking'}
              </button>
            </form>
          </>
        )}
      </div>

      <style jsx>{`
        .booking-container input::placeholder,
        .booking-container select,
        .booking-container textarea::placeholder {
          color: rgba(255, 255, 255, 0.7);
        }
        .booking-container select option {
          background: #2f4f2f;
          color: white;
        }
        @media (max-width: 768px) {
          .booking-container h2 {
            font-size: 2rem !important;
          }
          .booking-container {
            justify-content: center !important;
            padding-right: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Booking;